import { useCallback, useState } from "react";

import { ComboBoxControl } from "@/Common/Components/Inputs/ComboboxControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ConsentAction } from "./Actions/ConsentAction";

import styles from "./AccessWithoutConsentDialog.module.scss";

interface IAccessWithoutConsentDialogProps {
    patientContactFacetId: string;
    consentProgramId: number;
    onClose: () => void;
    onBack: () => void;
    onSave: (claimId: string) => void;
}

function AccessWithoutConsentDialog({ patientContactFacetId, consentProgramId, onBack, onClose, onSave }: IAccessWithoutConsentDialogProps): JSX.Element {
    const { getText } = useTranslation();
    const [selectedConsentMotive, setSelectedConsentMotive] = useState(cacheStore.getConsentMotiveLookups()[0].LookupID);
    const [comments, setComments] = useState(null);

    const onChangeConsentMotive = (e: React.SetStateAction<string>): void => {
        setSelectedConsentMotive(e);
    };

    const onCommentsChange = (value: string): void => {
        setComments(value);
    };

    const onSaveAccessWithoutConsent = useCallback(async (patientContactFacetId: string, motiveLookupId: string, comments: string, consentProgramId: number) => {
        const action = new ConsentAction();
        const response = await action.saveAccessWithoutConsent(patientContactFacetId, motiveLookupId, comments, consentProgramId);
        if (response != null) {
            onSave(response.ClaimId);
        }
    }, [onSave]);

    const footer = React.useMemo(() => (
        <div className="p-footer">
            <div className="text-right">
                <button className="btn btn-default push-right-5" onClick={() => onBack()}>{getText("BackLabel")}</button>
                <button
                    className="btn btn-danger"
                    onClick={() => onSaveAccessWithoutConsent(patientContactFacetId, selectedConsentMotive, comments, consentProgramId)}
                >{getText("AccessWithoutConsentLabel")}
                </button>
            </div>
        </div>
    ), [onBack, getText, patientContactFacetId, consentProgramId, comments, selectedConsentMotive, onSaveAccessWithoutConsent]);

    return (
        <Modal
            showX
            centerContent
            title={getText("AccessWithoutConsentLabel")}
            onClose={onClose}
            footer={footer}
            disableOverlayClose
            showOverlay
        >
            <div className="p-body">
                <div className={styles.label}>
                    <p>{getText("RequiredMotiveToAccessThePatientInformationWithoutTheirConsent")}</p>
                </div>
                <div className={styles.section}>
                    <div className={styles.label}>
                        <strong>{getText("MotiveLabel")} </strong>
                    </div>
                    <ComboBoxControl
                        id="consentProgramsField"
                        hasEmptyItem={false}
                        items={Immutable.List(cacheStore.getConsentMotiveLookups().map(x => x.ToComboboxItem()))}
                        selectedCode={selectedConsentMotive}
                        onChange={(selectedCode: string) => onChangeConsentMotive(selectedCode)}
                    />
                </div>
                <div className={styles.textbox}>
                    <div className={styles.label}>
                        <strong>{getText("AdditionalComments")} </strong>
                    </div>
                    <TextAreaResizable
                        id="consentNoteField"
                        text={comments}
                        isDisabled={false}
                        verticalResizeOnly
                        setCursorStartOnFocus={false}
                        resetTextOnIdChange
                        onChange={value => onCommentsChange(value)}
                        style={{ maxHeight: "144px" }}
                        maxLength={256}
                        showCounter
                    />
                </div>
            </div>
        </Modal>
    );
}

export { AccessWithoutConsentDialog };
